import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaEnvelope, FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import Cookies from "js-cookie";
import { formatPhoneNumber } from "../../utils/phone";
import "../../styles/Login.css";

export default function Register() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [msg, setMsg] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    setSuccess("");

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password) {
      setMsg("Please fill in First name, Last name, Email, and Password.");
      return;
    }
    if (password.length < 8) {
      setMsg("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setMsg("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      const full_name = `${firstName} ${lastName}`.trim();
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get("redirect");
      const pendingJobId =
        Cookies.get("pendingJobId") ||
        (redirect?.startsWith("/apply/")
          ? redirect.split("/apply/")[1]?.split("?")[0]
          : "");

      await axios.post(`${BASE_URL}/api/auth/register`, {
        email,
        mobile: formatPhoneNumber(mobile),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        full_name,
        pending_job_id: pendingJobId ? Number(pendingJobId) : null,
      });

      setSuccess("✔️ Verification email sent. Please check your inbox.");
      setResendEmail(email.trim().toLowerCase());
      setResendCooldown(60);
    } catch (err) {
      if (err.response?.data?.pendingVerification) {
        setSuccess("Your account was saved, but the verification email could not be sent.");
        setResendEmail(email.trim().toLowerCase());
        setResendCooldown(
          Number(err.response.data.retryAfterSeconds) || 60
        );
      }
      setMsg(err.response?.data?.message || err.message || "Register failed");
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    if (!resendEmail || resendCooldown > 0) return;

    try {
      setMsg("");
      setResendLoading(true);

      const { data } = await axios.post(
        `${BASE_URL}/api/auth/resend-verification`,
        { email: resendEmail }
      );

      setSuccess(
        data?.message || "✔️ Verification email resent. Please check your inbox."
      );
      setResendCooldown(Number(data?.retryAfterSeconds) || 60);
    } catch (err) {
      const retryAfterSeconds = Number(
        err.response?.data?.retryAfterSeconds || 0
      );

      if (retryAfterSeconds > 0) {
        setResendCooldown(retryAfterSeconds);
      }

      setMsg(
        err.response?.data?.message ||
          err.message ||
          "Failed to resend verification email"
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="auth-bg">
      <div className="login-card">
        <h3 className="auth-title">
          {success ? "Check your email" : "Create account"}
        </h3>

        {!success && <form onSubmit={submit} className="auth-form" noValidate>
          <label>First name</label>
          <input
            className="form-control"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Jane"
            required
            autoComplete="given-name"
          />

          <label>Last name</label>
          <input
            className="form-control"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            required
            autoComplete="family-name"
          />

          <label>Email</label>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
            required
            autoComplete="email"
          />

          <label>Mobile</label>
          <input
            type="tel"
            className="form-control"
            value={mobile}
            onChange={(e) => setMobile(formatPhoneNumber(e.target.value))}
            placeholder="(403) 123-4567"
            required
            pattern="^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$"
            title="Use format like (403) 123-4567"
            autoComplete="tel"
          />

          <label>Password</label>
          <div className="input-group">
            <input
              type={showPw ? "text" : "password"}
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              minLength={8}
              placeholder="At least 8 characters"
            />
            <button
              type="button"
              className="input-group-text"
              onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <label>Confirm Password</label>
          <input
            type={showPw ? "text" : "password"}
            className="form-control"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
            minLength={8}
            placeholder="Re-enter password"
          />

          <button
            type="submit"
            className="btn btn-primary auth-submit"
            disabled={loading}
            style={{ marginTop: 10 }}
          >
            {loading ? "Creating..." : "Create"}
          </button>
        </form>}

        {msg && <p className="auth-error">{msg}</p>}

        {success && (
          <div className="verification-panel" role="status">
            <div className="verification-icon" aria-hidden="true">
              <FaEnvelope />
            </div>
            <p className="verification-heading">Verification email sent</p>
            <p className="verification-copy">
              We sent a verification link to <strong>{resendEmail}</strong>.
              Open the link to finish creating your account.
            </p>
            <div className="verification-resend">
              <span>Didn't receive it?</span>
              <button
                type="button"
                className="resend-link"
                onClick={resendVerificationEmail}
                disabled={resendLoading || resendCooldown > 0}
              >
                {resendLoading
                  ? "Sending..."
                  : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend email"}
              </button>
            </div>
          </div>
        )}
        <div className="auth-links">
          Already have an account?{" "}
          <Link to="/login" className="link-cta">
            Login
          </Link>
        </div>
      </div>
    </div>
  );
}
